import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Users, TrendingUp, FileText, DollarSign, IndianRupee, Plus, Send, Building, UserCheck } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency as marketFormatCurrency, getCurrencySymbol } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RMDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [families, setFamilies] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRMData();
  }, []);

  const fetchRMData = async () => {
    try {
      const [usersRes, statsRes, familiesRes, txnRes] = await Promise.all([
        axios.get(`${API}/rm/users`),
        axios.get(`${API}/rm/stats`),
        axios.get(`${API}/families`),
        axios.get(`${API}/transactions/recent`).catch(() => ({ data: [] }))
      ]);

      // Filter only clients for the main list (exclude prospects)
      const allUsers = usersRes.data;
      setUsers(allUsers.filter(u => u.user_type === 'client'));
      setStats(statsRes.data);
      
      // Set families data
      if (familiesRes.data && familiesRes.data.families) {
        setFamilies(familiesRes.data.families);
      }

      // Set recent transactions
      if (Array.isArray(txnRes.data)) {
        setRecentActivity(txnRes.data);
      }
    } catch (error) {
      console.error('Error fetching RM data:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (userId) => {
    try {
      await axios.post(`${API}/reports/generate`, { user_id: userId });
      alert('Report generated and sent to user!');
      fetchRMData(); // Refresh data
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + (error.response?.data?.detail || error.message));
    }
  };

  // News/Insights carousel data (mock)
  const newsItems = [
    {
      title: "India is said to be an 'oasis' in the desert",
      author: "Charmaine Jacob",
      image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=400&h=250&fit=crop",
    },
    {
      title: "NFO review: WhiteOak Capital Mid Cap Fund",
      type: "NFO Review",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=250&fit=crop",
    },
    {
      title: "ASK Indian Entrepreneur Portfolio",
      type: "ASK Investment Managers",
      image: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=250&fit=crop",
    },
  ];

  // Recent activity is now fetched from /api/transactions/recent (see fetchRMData)

  // Asset Allocation Chart (aggregated)
  const getAssetAllocationChart = () => {

    // Aggregate all portfolios (simplified - would need actual data)
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(255, 216, 122, 0.3)',
        textStyle: { color: '#1f2937' }
      },
      legend: {
        orient: 'horizontal',
        bottom: '5%',
        left: 'center',
        textStyle: { 
          color: '#6b7280',
          fontSize: 11
        },
        itemGap: 10,
        itemWidth: 12,
        itemHeight: 12
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
              color: '#1f2937'
            }
          },
          labelLine: {
            show: false
          },
          data: [
            { value: 56.09, name: 'Equity',        itemStyle: { color: '#FED879' } },
            { value: 31.12, name: 'Debt',          itemStyle: { color: '#CCD5AE' } },
            { value: 8.74,  name: 'International', itemStyle: { color: '#CEDEAB' } },
            { value: 2.43,  name: 'Alternates',    itemStyle: { color: '#DEE4DA' } },
            { value: 1.63,  name: 'Liquid',        itemStyle: { color: '#CFCECE' } }
          ]
        }
      ]
    };
  };

  // Return Summary Chart
  const getReturnSummaryChart = () => {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(255, 216, 122, 0.3)',
        textStyle: { color: '#1f2937' }
      },
      legend: {
        data: ['Portfolio', 'Benchmark'],
        textStyle: { 
          color: '#6b7280',
          fontSize: 11
        },
        top: '5%',
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 15
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '12%',
        top: '20%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: ['MTD', 'YTD', '1Y', 'SI'],
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { 
          color: '#6b7280',
          fontSize: 11
        }
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { 
          color: '#6b7280',
          formatter: '{value}%',
          fontSize: 11
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [
        {
          name: 'Portfolio',
          type: 'bar',
          data: [0.95, 27.77, 37.31, 21.46],
          itemStyle: { color: '#FED879' },
          barWidth: '35%'
        },
        {
          name: 'Benchmark',
          type: 'bar',
          data: [0.22, 17.84, 24.29, 13.84],
          itemStyle: { color: '#CCD5AE' },
          barWidth: '35%'
        }
      ]
    };
  };

  const formatCurrency = (value) => marketFormatCurrency(value, user?.market);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{fontFamily: "'PerfectNineties', serif"}}>
              Welcome, {user?.full_name}
            </h1>
            <p className="text-sm text-gray-600">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          {/* <div className="flex gap-6">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Clients</p>
              <p className="text-2xl font-bold text-ugmonk-dark">{users.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Families</p>
              <p className="text-2xl font-bold text-ugmonk-dark">{families.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total AUM</p>
              <p className="text-2xl font-bold text-ugmonk-dark">
                {formatCurrency((stats?.total_aum || 0) + families.reduce((sum, f) => sum + (f.total_aum || 0), 0))}
              </p>
            </div> */}
          {/* </div> */}
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 stagger-in">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#fffdf0] rounded-lg">
                <UserCheck className="text-ugmonk-dark" size={24} />
              </div>
              <span className="text-2xl font-bold text-gray-900">{users.length}</span>
            </div>
            <p className="text-sm text-gray-600">Total Clients</p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#fffdf0] rounded-lg">
                <Building className="text-ugmonk-dark" size={24} />
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(families.reduce((sum, f) => sum + (f.total_aum || 0), 0))}
              </span>
            </div>
            <p className="text-sm text-gray-600">Family AUM</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#fffdf0] rounded-lg">
                <Users className="text-ugmonk-dark" size={24} />
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats?.total_aum || 0)}
              </span>
            </div>
            <p className="text-sm text-gray-600">Client AUM</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(254,216,121,0.25)' }}>
                {user?.market === 'US'
                  ? <DollarSign className="text-[#1a1a1a]" size={24} />
                  : <IndianRupee className="text-[#1a1a1a]" size={24} />
                }
              </div>
              <span className="text-2xl font-bold text-[#1a1a1a]">
                {formatCurrency((stats?.total_aum || 0) + families.reduce((sum, f) => sum + (f.total_aum || 0), 0))}
              </span>
            </div>
            <p className="text-sm text-[#404040] font-medium uppercase tracking-wide">Total AUM</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#fffdf0] rounded-lg">
                <TrendingUp className="text-ugmonk-dark" size={24} />
              </div>
              <span className="text-2xl font-bold text-green-600">+{((stats?.total_aum * 0.15) / (stats?.total_aum * 0.85) * 100).toFixed(1)}%</span>
            </div>
            <p className="text-sm text-gray-600">Average Returns</p>
          </div>
        </div>

        {/* Family Overview */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-900 font-semibold text-lg">Family Overview</h3>
            <button 
              onClick={() => navigate('/clients-prospects', { state: { tab: 'family-management' } })}
              className="text-sm text-ugmonk-dark hover:text-[#1a1a1a] font-medium"
            >
              View All →
            </button>
          </div>
          {families.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No families found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-in">
              {families.slice(0, 6).map((family) => (
                <div
                  key={family.family_id}
                  className="p-4 rounded-lg border border-gray-200 hover:border-[#ffd87a] card-glow cursor-pointer"
                  onClick={() => navigate('/clients-prospects', { state: { tab: 'family-management' } })}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{family.family_name}</h4>
                    <span className="text-xs px-2 py-1 rounded-full bg-[#fffdf0] text-ugmonk-darker">
                      {family.total_owners || 0} owners
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Head: {family.family_head}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">AUM</span>
                    <span className="text-sm font-semibold text-ugmonk-dark">
                      {formatCurrency(family.total_aum || 0)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Asset Allocation */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-4">Asset Allocation</h3>
            <div className="relative" style={{ minHeight: '280px' }}>
              <ReactECharts 
                option={getAssetAllocationChart()} 
                style={{ height: '280px', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
              <div className="absolute" style={{ top: '42%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                <p className="text-2xl font-bold text-gray-900 text-center whitespace-nowrap">
                  {formatCurrency((stats?.total_aum || 0) + families.reduce((sum, f) => sum + (f.total_aum || 0), 0))}
                </p>
              </div>
            </div>
          </div>

          {/* Return Summary */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-4">Return Summary</h3>
            <div style={{ minHeight: '280px' }}>
              <ReactECharts 
                option={getReturnSummaryChart()} 
                style={{ height: '280px', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {recentActivity.slice(0, 3).map((txn, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-3 last:border-0">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-sm text-gray-900 font-medium">{txn.fund}</p>
                      <p className="text-xs text-gray-500">{txn.investor} • {txn.family}</p>
                    </div>
                    <p className="text-sm text-gray-900 font-bold">{txn.value}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      txn.type === 'Buy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {txn.type}
                    </span>
                    <p className="text-xs text-gray-500">{txn.date}</p>
                  </div>
                </div>
              ))}
              <button 
                onClick={() => navigate('/clients-prospects')}
                className="text-xs text-ugmonk-dark hover:text-[#1a1a1a] w-full text-center pt-2 font-medium"
              >
                View All Activity →
              </button>
            </div>
          </div>
        </div>

        {/* All Clients List */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-900 font-semibold">All Clients ({users.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">Name</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">PAN</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">Mobile</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">Account No.</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">Portfolio Value</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3">Risk Profile</th>
                </tr>
              </thead>
              <tbody>
                {users.map((clientUser) => (
                  <tr key={clientUser.id} className="border-b border-gray-100 hover:bg-[#fffdf0]/50 transition-colors">
                    <td className="py-3 text-sm text-gray-900 font-medium">{clientUser.full_name}</td>
                    <td className="py-3 text-sm text-gray-900">{clientUser.pan || 'N/A'}</td>
                    <td className="py-3 text-sm text-gray-900">{clientUser.mobile || 'N/A'}</td>
                    <td className="py-3 text-sm text-gray-900">{clientUser.account_number || 'N/A'}</td>
                    <td className="py-3 text-sm text-gray-900 font-semibold">{formatCurrency(clientUser.portfolio_value)}</td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        clientUser.risk_profile === 'Conservative' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                        clientUser.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-[#fffdf0] text-ugmonk-dark'
                      }`}>
                        {clientUser.risk_profile}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RMDashboard;
